import { BaseFeatureFields, useFeaturesQuery } from './useFeaturesQuery';
import {Feature, GetFeaturesResponse} from '../../services/api-types';

/**
 * Interface defining the contract for base feature.
 */
export interface UseBaseFeature {
    readonly isLoadingError?: boolean;
    readonly isFeatureEnabled: (name: FeatureToggleName) => boolean;
    readonly isFeatureQueryLoading: boolean;
    readonly error?: any
    readonly featureResponse?: GetFeaturesResponse | undefined
}

/**
 * Hook for managing basic feature toggles
 * @returns Object containing methods to check feature status, its fields and loading state which depends on the useFeaturesQuery hook.
 */
export const useBaseFeature = (): UseBaseFeature => {
    const { data, isLoading, isLoadingError, error } = useFeaturesQuery();

    const isEnabled = (fields: BaseFeatureFields) => {
        const now = new Date();
        return new Date(fields.startDateTime) < now && now < new Date(fields.endDateTime ?? '2099-01-01');
    };

    const findByName = (name: FeatureToggleName): Feature | undefined => data?.features.find((feature) => {
        return feature.name === name
    });

    const isFeatureEnabled = (name: FeatureToggleName): boolean => {
        const feature = findByName(name);
        if (feature === undefined) {
            return false;
        }
        return isEnabled(feature.fields);
    };

    return {
        isLoadingError,
        isFeatureEnabled,
        isFeatureQueryLoading: isLoading,
        error,
        featureResponse: data,
    };
};

/**
 * Enum containing known feature toggle names.
 * Used to identify different features that can be toggled in the application.
 */
export enum FeatureToggleName {
    TOLL_PROVIDER_OUTAGE_ETOLL = 'TollProviderOutageEtoll',
    TOLL_PROVIDER_OUTAGE_LINKT = 'TollProviderOutageLinkt',
    TR2_DECOMMISSION = 'Tr2Decommission',
    ALERT_MESSAGE = 'AlertMessage',
}
